import { FilterOption } from '../../shared-component/app-filter-select/app-filter-select.component';

/**
 * The license statuses offered by the Status filter on Workspace, Reports, Documents and
 * Masters · License Renew.
 *
 * Hardcoded on purpose: the set never changes, and these strings are the WIRE CONTRACT — they are
 * sent verbatim as `status_list` to those pages' grid AND export endpoints, which filter by NAME
 * (not by the ids `/DropDown/get-status-list` returns). A name the backend does not recognise comes
 * back as an EMPTY GRID rather than an error, so a typo here fails silently. Guarded by
 * `license-status-options.constants.spec.ts`.
 *
 * `label` is display text; `value` is the wire string. They match verbatim EXCEPT
 * `PendingForReview`, whose label is humanised for display ("Pending For Review") while its
 * VALUE stays unspaced — the value is what the backend filters on, the same string the grid
 * returns in `mgr_status` and that `workspace-action-visibility.ts` branches on. Application
 * Rejected is the odd one out the OTHER way: the backend filter takes the SPACED
 * 'Application Rejected' (the unspaced 'ApplicationRejected' returns an empty grid), even
 * though `mgr_status` still reports the workflow state unspaced. Match each status's own
 * wire spelling — never assume value === mgr_status.
 *
 * Masters · License Renew is the single-select outlier: it offers this same list but sends
 * ONE selected name as `license_status` (`"Status"` = all) rather than a `status_list` array.
 * With that page migrated off it, the API-backed `FilterOptionsStore.statusOptions` has no
 * page consumer left.
 */
export const LICENSE_STATUS_OPTIONS: readonly FilterOption[] = [
  { label: 'Active',              value: 'Active' },
  { label: 'Expired',             value: 'Expired' },
  { label: 'Expiring',            value: 'Expiring' },
  { label: 'Applied',             value: 'Applied' },
  { label: 'Pending For Review',  value: 'PendingForReview' },
  { label: 'Rejected',            value: 'Rejected' },
  { label: 'Application Rejected', value: 'Application Rejected' },
  { label: 'Terminated',          value: 'Terminated' },
  { label: 'Not Applicable',      value: 'Not Applicable' },
] as const;

/**
 * The six live/pending status names, PINNED as an explicit allowlist — NOT computed from
 * the dashboard metrics' `SETTLED_STATUS_KEYS` (the two live in separate modules with no
 * shared import). Sent as the Workspace `status` scope by the By State ACTIVE-segment
 * drill-down (spec `2026-08-11-by-state-stacked-bar-drilldown-design.md` §3.d). DERIVED
 * from {@link LICENSE_STATUS_OPTIONS} so these can never drift from the canonical wire
 * spellings (rule 178) — a rename there empties out of here and the spec test fails.
 * That this pinned six-name list happens to be exactly the complement of
 * `SETTLED_STATUS_KEYS` over the canonical options is a property ENFORCED by a drift test
 * in `license-status-options.constants.spec.ts` (which imports `isSettledStatus`), not a
 * guarantee this module derives by construction.
 *
 * 2026-08-20 membership change (user spec): Expired and Rejected rejoined the live/pending
 * set — an expired or rejected license still needs renewal or closure — and Not Activated
 * joined the closed set instead. See `management-dashboard-metrics.ts`'s
 * `UNSETTLED_STATUS_NAMES`/`SETTLED_STATUS_NAMES`, the single source of that split.
 */
export const LIVE_LICENSE_STATUS_NAMES: readonly string[] = LICENSE_STATUS_OPTIONS
  // FilterOption.value is `string | number`; every LICENSE_STATUS_OPTIONS value is
  // actually a string, but the field's declared type still requires the coercion here.
  .filter((option) =>
    ['Active', 'Expired', 'Expiring', 'Applied', 'PendingForReview', 'Rejected'].includes(
      String(option.value),
    ),
  )
  .map((option) => String(option.value));

/**
 * The two statuses the By State **On track** segment counts, and the exact `status` scope
 * its drill sends to Workspace/Reports (user request 2026-08-31 — narrowed from the former
 * {@link LIVE_LICENSE_STATUS_NAMES}-without-'Active' five). Wire spellings DERIVED from
 * {@link LICENSE_STATUS_OPTIONS} (rule 178), same discipline as its two siblings above and
 * below: a rename there empties this list and the spec's drift test fails.
 *
 * Pinned against `management-dashboard-metrics.ts`'s `ON_TRACK_STATUS_NAMES` by a drift
 * test in this file's spec. The two modules deliberately share no import (same reason
 * {@link LIVE_LICENSE_STATUS_NAMES} is a pinned allowlist rather than a computed
 * complement), so the test is what keeps the counted cohort and the drilled cohort
 * identical — the segment's number must always match the grid its click opens.
 */
export const ON_TRACK_DRILL_STATUS_NAMES: readonly string[] = ['Active', 'Applied']
  .filter((name) => LICENSE_STATUS_OPTIONS.map((o) => String(o.value)).includes(name));

/**
 * The three CLOSED statuses the By Team "Closed" drill sends as the Workspace/Reports
 * `status` scope (2026-08-20 drill-down spec §5.b) — wire spellings DERIVED from
 * {@link LICENSE_STATUS_OPTIONS} (rule 178: 'Application Rejected' is SPACED on the wire).
 * 'Not Activated' is deliberately absent: it is settled in the metrics module but is not a
 * status option on these pages and their grids never serve those rows (own endpoint/page).
 * Pinned against the metrics module's `SETTLED_STATUS_NAMES` minus
 * `OUT_OF_SCOPE_STATUS_NAMES` by a drift test in this file's spec (the two modules
 * deliberately share no import — same discipline as {@link LIVE_LICENSE_STATUS_NAMES}).
 */
export const CLOSED_DRILL_STATUS_NAMES: readonly string[] = ['Terminated', 'Not Applicable', 'Application Rejected']
  .filter((name) => LICENSE_STATUS_OPTIONS.map((o) => String(o.value)).includes(name));
